<template>
  <div
    :class="[
      'flex flex-col justify-between overflow-hidden relative max-h-[256px]',
      variantClass,
      backgroundClass,
      extraClasses,
    ]"
    data-test-id="member_card"
    v-if="showMembershipCard"
  >
    <!--Card Header-->
    <div class="flex justify-between items-center" :class="headerPaddingClass">
      <template v-if="variant === 'thumbnail'">
        <img :src="defaultCompanyLogo" alt="RACQ" class="w-[26px] h-[7px]" />
      </template>
      <template v-else>
        <img :src="defaultCompanyLogo" alt="RACQ" class="w-[79.6px] h-[20.2px]" />
      </template>

      <template v-if="variant === 'thumbnail'">
        <img :src="levelLogoSrc" :alt="`${memberLoyaltyLevel} logo`" class="w-[10px] h-[4px]" />
      </template>
      <template v-else>
        <img :src="levelLogoSrc" :alt="`${memberLoyaltyLevel} logo`" />
      </template>
    </div>

    <!-- Card Body variants -->
    <div class="flex-1 flex relative z-[1]" :class="variant === 'thumbnail' ? '' : 'px-4'">
      <!-- Front Face -->
      <template v-if="variant === 'front'">
        <div class="flex flex-col items-start justify-center text-white z-[1]">
          <div>
            <p class="text-[10px] font-body text-shadow-1xl">{{ cardTitle }}</p>
            <p class="text-base text-shadow-1xl mb-[6px]">{{ memberCardNumber }}</p>
          </div>
          <p class="uppercase text-m mb-4 text-shadow-1xl">{{ memberName }}</p>
          <p class="font-body text-[10px] text-shadow-1xl">{{ joinDateLabel }}: {{ memberJoinedDate }}</p>
        </div>
      </template>

      <!-- Thumbnail -->
      <template v-else-if="variant === 'thumbnail'">
        <div class="flex flex-col space-y-2 z-[1] p-1">
          <img :src="defaultThumbPlaceholder" alt="{{ thumbnailAlt }}" class="max-w-full max-h-full" />
        </div>
      </template>

      <!-- Back Face -->
      <template v-else-if="variant === 'back'">
        <div class="absolute inset-0 flex flex-col items-center pb-4 px-4 z-[1]">
          <div class="bg-white w-full p-1 flex justify-center items-center rounded-md h-[100%]">
            <BaseBarcode :membershipNumber="memberCardNumber" class="max-w-full max-h-full" />
          </div>
        </div>
      </template>

      <!--Footer: smile stripe -->
      <div
        class="flex absolute w-[100%] h-[100%] bottom-0 z-[0]"
        :class="variant === 'thumbnail' ? 'right-[2.5rem]' : 'right-0'"
      >
        <img :src="smileStripeSrc" :alt="`${memberLoyaltyLevel} smile stripe`" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import BaseBarcode from '@/components/BarCode/BaseBarcode.vue';
import defaultCompanyLogo from '@/assets/racq-logo.svg';
import defaultThumbPlaceholder from '@/assets/card-skeleton.svg';
import blue from '@/assets/loyalty-logos/Blue.svg';
import bronze from '@/assets/loyalty-logos/Bronze.svg';
import silver from '@/assets/loyalty-logos/Silver.svg';
import gold from '@/assets/loyalty-logos/Gold.svg';
import gold50 from '@/assets/loyalty-logos/Gold50.svg';
import smileBlue from '@/assets/card-stripes/smile-blue.png';
import smileBronze from '@/assets/card-stripes/smile-bronze.png';
import smileSilver from '@/assets/card-stripes/smile-silver.png';
import smileGold from '@/assets/card-stripes/smile-gold.png';
import smileGold50 from '@/assets/card-stripes/smile-gold50.png';
import { useAccountStore } from '@/store';
import { toFormattedCardNumber } from '@/utils/convert';
import { format as formatDate } from 'date-fns';
import { CardVariant } from '@/enums/membership-card-variant.enum';
import { LoyaltyLevels } from '@/enums/loyalty-levels.enum';
import { hasLoyaltyLevel } from '@/utils/loyalty-level';

interface Props {
  /** visual variant */
  variant?: CardVariant;
  level?: string;
  /** copy */
  cardTitle?: string; // e.g., "MEMBERSHIP CARD No."
  joinDateLabel?: string; // e.g., "JOIN DATE"
  /** member data */
  displayMemberNumber?: string;
  extraClasses?: string;
}

const accountStore = useAccountStore();

const props = withDefaults(defineProps<Props>(), {
  variant: CardVariant.FRONT,
  level: undefined,
  cardTitle: 'MEMBERSHIP CARD No.',
  joinDateLabel: 'JOIN DATE',
  displayMemberNumber: '',
  extraClasses: '',
});

const defaultLevelLogos: Record<string, string> = {
  '':'',
  blue,
  bronze,
  silver,
  gold,
  gold50,
};
const defaultSmileStripes: Record<string, string> = {
  ['']: '',
  [LoyaltyLevels.Blue]: smileBlue,
  [LoyaltyLevels.Bronze]: smileBronze,
  [LoyaltyLevels.Silver]: smileSilver,
  [LoyaltyLevels.Gold]: smileGold,
  [LoyaltyLevels.Gold50]: smileGold50,
};

const headerPaddingClass = computed(() =>
  props.variant === 'thumbnail' ? 'p-1' : props.variant === 'front' ? 'px-4 py-3' : 'px-4 py-5'
);

const backgroundClassMap: Record<string, string> = {
  '':'',
  [LoyaltyLevels.Blue]: 'bg-gradient-blue-tier',
  [LoyaltyLevels.Bronze]: 'bg-gradient-bronze-tier',
  [LoyaltyLevels.Silver]: 'bg-gradient-silver-tier',
  [LoyaltyLevels.Gold]: 'bg-gradient-gold-tier',
  [LoyaltyLevels.Gold50]: 'bg-gradient-gold-highlight',
};

const backgroundClass = computed(() => backgroundClassMap[memberLoyaltyLevel.value]);

const variantClass = computed(() => {
  switch (props.variant) {
    case 'front':
      return 'w-[260px] h-[162px] rounded-xl shadow-2xl';
    case 'thumbnail':
      return 'w-[86px] h-[53.5px] rounded-md shadow-1xl';
    case 'back':
      return 'w-[auto] aspect-racq-card rounded-xl shadow-1xl';
    default:
      return 'w-[260px] h-[162px] rounded-xl shadow-1xl';
  }
});

const levelLogoSrc = computed(
  () => defaultLevelLogos[memberLoyaltyLevel.value] ?? defaultLevelLogos[LoyaltyLevels.None]
);
const smileStripeSrc = computed(
  () => defaultSmileStripes[memberLoyaltyLevel.value] ?? defaultLevelLogos[LoyaltyLevels.None]
);

const memberCardNumber = computed(() =>
  toFormattedCardNumber(accountStore.identityDetails?.membershipCardNumber || '')
);
const memberLoyaltyLevel = computed(
  () => props.level || accountStore.identityDetails?.membershipLoyalty?.toLowerCase() || LoyaltyLevels.None
);
const memberJoinedDate = computed(() =>
  accountStore.identityDetails && accountStore.identityDetails.membershipJoined
    ? formatDate(accountStore.identityDetails.membershipJoined, 'yyyy')
    : ''
);
const memberName = computed(() => {
  return `${accountStore.identityDetails?.firstName} ${accountStore.identityDetails?.lastName ?? ''}`;
});
const showMembershipCard = computed(() => hasLoyaltyLevel(memberLoyaltyLevel.value));
</script>
